// New Loyalty Program Service using real backend API
import {
  customerApi,
  transactionApi,
  loyaltyProgramApi,
  notificationApi,
  pointsApi,
  promotionsApi,
  loyaltyAnalyticsApi,
} from "./backend-loyalty.api";
import {
  CustomerDto,
  CustomerDetailDto,
  CreateCustomerDto,
  TransactionDto,
  TransactionDetailDto,
  AwardPointsDto,
  RedeemPointsDto,
  ManualAdjustmentDto,
  LoyaltyProgramDetailDto,
  UpdateLoyaltyProgramDto,
  CreateLoyaltyProgramDto,
  PointsReminderRequestDto,
  TierProgressNotificationRequestDto,
  PromotionalNotificationRequestDto,
  TransactionType,
} from "../types/backend-api.types";

// Legacy types for compatibility (mapping from old types to new types)
import {
  Customer,
  LoyaltyTransaction,
  Reward,
  Promotion,
  CustomerAnalytics,
  GameificationChallenge,
  CustomerSegment,
  LoyaltyProgramSettings,
  CustomerRegistrationData,
  CustomerSearchResult,
  PointsTransactionRequest,
  DashboardStats,
  RecentActivity,
  PromotionalCampaign,
  CampaignFilters,
  MessageTemplate,
  CampaignRecipient,
  CampaignDashboardStats,
} from "../types/loyalty.types";

import { jwtDecode } from "jwt-decode";

// Helper function to get tier icon
const getTierIcon = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.includes("bronze")) return "🥉";
  if (lower.includes("silver")) return "🥈";
  if (lower.includes("gold")) return "🥇";
  if (lower.includes("platinum")) return "💎";
  if (lower.includes("diamond")) return "👑";
  return "🏅";
};

// Helper function to map tiers to legacy format
const mapTiersToLegacyFormat = (dtos: import("../types/backend-api.types").LoyaltyTierDto[]): import("../types/loyalty.types").LoyaltyTier[] => {
  const sortedDetails = [...dtos].sort((a, b) => a.minimumPoints - b.minimumPoints);

  return sortedDetails.map((dto, index) => {
    const nextTier = sortedDetails[index + 1];
    // Subtract 1 so the upper bound is exclusive-adjacent: Bronze 50–99, Silver 100–149, etc.
    // This matches exactly what the user configured and prevents the visual overlap that
    // occurs when max = nextTier.min (both tiers share the boundary point in the display).
    const maxPoints = nextTier ? nextTier.minimumPoints - 1 : 1_000_000;

    return {
      id: dto.tierId,
      name: dto.tierName,
      minPoints: dto.minimumPoints,
      maxPoints: maxPoints,
      benefits: dto.description ? dto.description.split("|").filter(Boolean) : [],
      multiplier: dto.pointsMultiplier,
      color: dto.colorHex || "#CD7F32",
      icon: getTierIcon(dto.tierName)
    };
  });
};

type LegacyLoyaltyTier = import("../types/loyalty.types").LoyaltyTier;

const TIER_CACHE_TTL_MS = 60_000;
const tierCacheByRestaurant = new Map<number, { expiresAt: number; tiers: LegacyLoyaltyTier[] }>();
const tierRequestByRestaurant = new Map<number, Promise<LegacyLoyaltyTier[]>>();

const cloneLegacyTiers = (tiers: LegacyLoyaltyTier[]): LegacyLoyaltyTier[] =>
  tiers.map((tier) => ({
    ...tier,
    benefits: Array.isArray(tier.benefits) ? [...tier.benefits] : [],
  }));

const getConfiguredTiersWithCache = async (
  currentToken: string,
  currentRestaurantId: number,
  forceRefresh: boolean = false
): Promise<LegacyLoyaltyTier[]> => {
  if (!currentRestaurantId) return [];

  const now = Date.now();

  if (!forceRefresh) {
    const cached = tierCacheByRestaurant.get(currentRestaurantId);
    if (cached && cached.expiresAt > now) {
      return cloneLegacyTiers(cached.tiers);
    }

    const inFlight = tierRequestByRestaurant.get(currentRestaurantId);
    if (inFlight) {
      const tiers = await inFlight;
      return cloneLegacyTiers(tiers);
    }
  }

  const request = loyaltyProgramApi
    .getLoyaltyTiers(currentToken, currentRestaurantId)
    .then((tiersDto) => {
      const mapped = Array.isArray(tiersDto) && tiersDto.length > 0
        ? mapTiersToLegacyFormat(tiersDto)
        : [];

      tierCacheByRestaurant.set(currentRestaurantId, {
        tiers: mapped,
        expiresAt: Date.now() + TIER_CACHE_TTL_MS,
      });

      return mapped;
    })
    .catch(() => {
      const stale = tierCacheByRestaurant.get(currentRestaurantId);
      return stale?.tiers ?? [];
    })
    .finally(() => {
      tierRequestByRestaurant.delete(currentRestaurantId);
    });

  tierRequestByRestaurant.set(currentRestaurantId, request);
  const tiers = await request;
  return cloneLegacyTiers(tiers);
};

const invalidateTierCache = (restaurantId?: number) => {
  if (!restaurantId) return;
  tierCacheByRestaurant.delete(restaurantId);
  tierRequestByRestaurant.delete(restaurantId);
};

// Helper function to map customers to legacy format
const mapCustomerToLegacyFormat = (customer: CustomerDto | CustomerDetailDto): Customer => {
  return {
    id: customer.customerId,
    name: customer.fullName,
    email: customer.email || "",
    phone: customer.phoneNumber,
    avatar: `https://i.pravatar.cc/150?u=${customer.customerId}`,
    dateJoined: customer.registrationDate.toString(),
    lastVisit:
      customer.lastVisitDate?.toString() ||
      customer.registrationDate.toString(),
    totalVisits: customer.visitCount || 0,
    totalSpent: customer.totalSpent || 0,
    loyaltyTier: (() => {
      const possibleTier =
        (customer as any).tier ||
        (customer as any).currentTier ||
        (customer as any).loyaltyTierName ||
        (customer as any).tierName;
      if (!possibleTier) return null;
      const name = String(possibleTier);
      return {
        id: name.toLowerCase(),
        name,
        minPoints: 0,
        maxPoints: 999999,
        benefits: [],
        multiplier: 1,
        color: "#6B7280",
        icon: getTierIcon(name),
      } as any;
    })(),
    points: customer.currentPointBalance,
    preferences: {
      favoriteItems: [],
      dietaryRestrictions: [],
      allergies: [],
      spiceLevel: "medium",
      preferredTimeSlots: [],
      communicationPreference: "email",
    },
    demographics: {
      age: undefined,
      gender: undefined,
      location: undefined,
      occupation: undefined,
      birthdate: (customer as CustomerDetailDto).dateOfBirth?.toString(),
    },
  };
};

// Helper function to map transactions to legacy format
const mapTransactionsToLegacyFormat = (
  transactions: TransactionDto[]
): LoyaltyTransaction[] => {
  if (!Array.isArray(transactions)) return [];
  
  return transactions.map((transaction) => {
    // Robust type checking for transaction type (Enum value 1=EARNED)
    const tType = Number(transaction.transactionType);
    let type: "earn" | "redeem" | "adjust" = "redeem"; // Default
    
    // Check against Enum or known values
    if (tType === 1 || tType === TransactionType.EARNED || (transaction as any).transactionTypeName === 'EARNED') {
        type = "earn";
    } else if (tType === 4 || tType === TransactionType.MANUAL_ADJUSTMENT || (transaction as any).transactionTypeName === 'MANUAL_ADJUSTMENT') {
        type = "adjust";
    }
    
    return {
    id: transaction.transactionId || (transaction as any).id || `trans_${Math.random()}`,
    customerId: transaction.customerId,
    type,
    points: transaction.pointsChanged !== undefined ? transaction.pointsChanged : (transaction as any).points || 0,
    orderId: transaction.description?.includes("Order")
      ? "order_" + Math.random().toString(36).substr(2, 9)
      : undefined,
    description: transaction.description || "Transaction",
    timestamp: transaction.transactionDate ? transaction.transactionDate.toString() : new Date().toISOString(),
    expiryDate: transaction.expiryDate?.toString(),
    }
  });
};

const normalizeDateInputValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      return trimmedValue;
    }
  }

  const parsedDate = new Date(value as any);
  if (Number.isNaN(parsedDate.getTime())) {
    const fallbackValue = String(value).trim();
    return fallbackValue.length >= 10 ? fallbackValue.slice(0, 10) : undefined;
  }

  return parsedDate.toISOString().slice(0, 10);
};

// Helper function to map rewards to legacy format
const mapRewardsToLegacyFormat = (rewards: any[]): Reward[] => {
  return rewards.map((reward) => ({
    id: reward.rewardId,
    name: reward.rewardName,
    description: reward.description,
    type:
      reward.rewardType === 1
        ? "discount"
        : reward.rewardType === 2
          ? "freeItem"
          : "bonus",
    pointsCost: reward.requiredPoints,
    value: reward.rewardValue,
    image: undefined,
    terms: reward.termsAndConditions ? [reward.termsAndConditions] : [],
    validUntil: normalizeDateInputValue(reward.validUntil),
    maxRedemptions: reward.maxRedemptionsPerCustomer,
    currentRedemptions: 0,
    isActive: reward.isActive,
    eligibleTiers: Array.isArray(reward.eligibleTiers) ? reward.eligibleTiers : [],
  }));
};

const normalizeMaxRedemptions = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const numericValue =
    typeof value === "number" ? value : Number(String(value).trim());

  if (!Number.isFinite(numericValue)) {
    return undefined;
  }

  const integerValue = Math.trunc(numericValue);
  return integerValue > 0 ? integerValue : undefined;
};

// Helper function to map loyalty program to settings.
// NOTE: tiers are intentionally empty here — callers must overlay the real
// tiers fetched from getLoyaltyTiers() to get accurate thresholds.
const mapLoyaltyProgramToSettings = (
  program: LoyaltyProgramDetailDto
): LoyaltyProgramSettings => {
  return {
    pointsPerRupee: program.pointsPerRupee,
    pointsExpireDays: program.pointExpiryDays,
    referralPointsReferrer: program.referralPointsReferrer ?? 0,
    referralPointsReferee: program.referralPointsReferee ?? 0,
    reviewPoints: program.reviewPoints ?? 0,
    birthdayPoints: program.birthdayPoints ?? 0,
    signupBonus: program.signupBonus ?? 0,
    isConfigured: program.isConfigured ?? false,
    tiers: [], // populated by getLoyaltyTiers() API call
    minimumRedemptionPoints: program.minimumRedemptionPoints ?? 0,
    minimumOrderAmount: program.minimumOrderAmount,
    equivalentLoyaltyPoints: program.equivalentLoyaltyPoints ?? 100,
    rupeesDiscountValue: program.rupeesDiscountValue ?? 50,
    isActive: program.isActive,
  };
};

// Helper to get runtime auth context
const getAuthContext = (token?: string, restaurantId?: number) => {
  let currentToken = token || "";
  let currentRestaurantId = restaurantId || 0;
  let currentUserName = "";

  if (!currentToken && typeof window !== "undefined") {
    currentToken = localStorage.getItem("authToken") || "";
  }

  if (currentToken) {
    try {
      const decoded: any = jwtDecode(currentToken);
      if (decoded.restaurantId && !currentRestaurantId) {
        currentRestaurantId = parseInt(decoded.restaurantId, 10);
      }
      if (decoded.userName) {
        currentUserName = decoded.userName;
      } else if (decoded.email) {
        currentUserName = decoded.email;
      }
    } catch (error) {
      // Silently continue if JWT decode fails
    }
  }

  if (!currentUserName && typeof window !== "undefined") {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.userName) {
          currentUserName = user.userName;
        } else if (user.email) {
          currentUserName = user.email;
        }
      }
    } catch (error) {
      // Silently continue
    }
  }

  if (!currentRestaurantId && typeof window !== "undefined") {
    const storedId = localStorage.getItem("businessId") || localStorage.getItem("restaurantId");
    if (storedId) {
      currentRestaurantId = parseInt(storedId, 10);
    }
  }

  return { currentToken, currentRestaurantId, currentUserName };
};

// Plain object API (like inventoryApi)
export const loyaltyApi = {
  // Customer Management
  async getCustomers(page: number = 1, size: number = 50, token?: string, restaurantId?: number): Promise<Customer[]> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
      try {
      const [result, configuredTiers] = await Promise.all([
        customerApi.getCustomers(currentToken, page, size, currentRestaurantId),
        getConfiguredTiersWithCache(currentToken, currentRestaurantId),
      ]);

      const sortedTiers = configuredTiers.length > 0
        ? [...configuredTiers].sort((a, b) => b.minPoints - a.minPoints)
        : null;

      return result.items.map((dto) => {
        const customer = mapCustomerToLegacyFormat(dto);
        if (!sortedTiers) return customer;
        const points = dto.currentPointBalance ?? 0;
        const matched = sortedTiers.find((t) => points >= t.minPoints) ?? null;
        return { ...customer, loyaltyTier: matched };
      });
    } catch (error) {
      return [];
    }
  },

  async getCustomerById(customerId?: string, token?: string, restaurantId?: number): Promise<Customer | null> {
    if (!customerId) return null;
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const [customer, configuredTiers] = await Promise.all([
        customerApi.getCustomerDetails(currentToken, customerId),
        getConfiguredTiersWithCache(currentToken, currentRestaurantId),
      ]);
      const mapped = mapCustomerToLegacyFormat(customer);
      if (configuredTiers.length > 0) {
        const sorted = [...configuredTiers].sort((a, b) => b.minPoints - a.minPoints);
        const points = (customer as any).currentPointBalance ?? 0;
        const matched = sorted.find((t) => points >= t.minPoints) ?? null;
        return { ...mapped, loyaltyTier: matched };
      }
      return mapped;
    } catch (error) {
      return null;
    }
  },

  async updateCustomer(
    customerId?: string,
    updateData?: {
      fullName: string;
      email: string;
      phoneNumber: string;
      dateOfBirth: string | null;
      isActive: boolean;
    },
    token?: string,
    restaurantId?: number
  ): Promise<Customer | null> {
    if (!customerId || !updateData) return null;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      const dto = {
        fullName: updateData.fullName,
        email: updateData.email,
        phoneNumber: updateData.phoneNumber,
        dateOfBirth: updateData.dateOfBirth
          ? new Date(updateData.dateOfBirth)
          : undefined,
        isActive: updateData.isActive,
      };

      const updated = await customerApi.updateCustomer(
        currentToken,
        customerId,
        dto
      );
      return mapCustomerToLegacyFormat(updated);
    } catch (error) {
      throw error;
    }
  },

  async deleteCustomer(customerId?: string, token?: string, restaurantId?: number): Promise<boolean> {
    if (!customerId) return false;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      await customerApi.deleteCustomer(currentToken, customerId);
      return true;
    } catch (error) {
      return false;
    }
  },

  async updateCustomerPoints(
    customerId?: string,
    points?: number,
    description?: string,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!customerId || points === undefined) return false;
    const { currentToken, currentRestaurantId, currentUserName } = getAuthContext(token, restaurantId);
    try {
      const adjustmentData: ManualAdjustmentDto = {
        customerId,
        restaurantId: currentRestaurantId,
        pointsChanged: points,
        description: description || "Manual points adjustment",
        createdBy: currentUserName,
      };
      await transactionApi.manualAdjustment(currentToken, adjustmentData);
      return true;
    } catch (error) {
      return false;
    }
  },

  // Loyalty Transactions
  async getLoyaltyTransactions(
    customerId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<LoyaltyTransaction[]> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      let result;
      if (customerId) {
        result = await customerApi.getCustomerTransactions(
          currentToken,
          customerId
        );
      } else {
        result = await transactionApi.getTransactions(
          currentToken,
          currentRestaurantId
        );
      }
      // Handle both PagedResult ({ items: [...] }) and raw Array ([...]) responses
      const items = Array.isArray(result) ? result : (result?.items || []);
      
      const mapped = mapTransactionsToLegacyFormat(items);
      
      return mapped;
    } catch (error) {
      return [];
    }
  },

  async addLoyaltyTransaction(
    transaction?: Omit<LoyaltyTransaction, "id">,
    token?: string,
    restaurantId?: number
  ): Promise<string> {
    if (!transaction) throw new Error("Transaction data required");
    const { currentToken, currentRestaurantId, currentUserName } = getAuthContext(token, restaurantId);
    try {
      if (transaction.type === "earn") {
        const awardData: AwardPointsDto = {
          customerId: transaction.customerId,
          restaurantId: currentRestaurantId,
          orderAmount: transaction.points * 10,
          description: transaction.description,
          createdBy: currentUserName,
        };
        const result = await transactionApi.awardPoints(currentToken, awardData);
        return result.transactionId;
      } else {
        const adjustmentData: ManualAdjustmentDto = {
          customerId: transaction.customerId,
          restaurantId: currentRestaurantId,
          pointsChanged: -transaction.points,
          description: transaction.description,
          createdBy: currentUserName,
        };
        const result = await transactionApi.manualAdjustment(
          currentToken,
          adjustmentData
        );
        return result.transactionId;
      }
    } catch (error) {
      throw error;
    }
  },

  // Rewards Management
  async getRewards(
    token?: string,
    restaurantId?: number,
    includeInactive: boolean = false
  ): Promise<Reward[]> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const rewards = await loyaltyProgramApi.getRewards(
        currentToken,
        currentRestaurantId,
        includeInactive
      );

      return mapRewardsToLegacyFormat(rewards || []);
    } catch (error) {
      return [];
    }
  },

  async redeemReward(customerId?: string, rewardId?: string, orderId?: number, token?: string, restaurantId?: number): Promise<boolean> {
    if (!customerId || !rewardId || !orderId) return false;
    const { currentToken, currentUserName } = getAuthContext(token, restaurantId);
    try {
      const redeemData: RedeemPointsDto = {
        customerId,
        rewardId,
        orderId,
        description: "Reward redemption",
        createdBy: currentUserName,
      };
      await transactionApi.redeemPoints(currentToken, redeemData);
      return true;
    } catch (error) {
      return false;
    }
  },

  async previewRedemption(customerId?: string, rewardId?: string, token?: string, restaurantId?: number): Promise<any> {
    if (!customerId || !rewardId) return null;
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const preview = await pointsApi.previewRedemption(currentToken, {
        restaurantId: currentRestaurantId,
        customerId,
        rewardId,
      });

      return preview;
    } catch (error) {
      return null;
    }
  },

  async getAvailableRewardsForCustomer(customerId?: string, token?: string, restaurantId?: number) {
    if (!customerId) return [];
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      const resp = await loyaltyProgramApi.getAvailableRewardsForCustomer(
        currentToken,
        customerId
      );
      return mapRewardsToLegacyFormat(resp.availableRewards || []);
    } catch (error) {
      return [];
    }
  },

  async createReward(
    reward?: Omit<Reward, "id" | "currentRedemptions">,
    token?: string,
    restaurantId?: number
  ): Promise<string> {
    if (!reward) throw new Error("Reward data required");
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const normalizedMaxRedemptions = normalizeMaxRedemptions(
        reward.maxRedemptions
      );
      const normalizedValidUntil = normalizeDateInputValue(reward.validUntil);

      const createDto = {
        restaurantId: currentRestaurantId,
        rewardName: reward.name,
        description: reward.description,
        requiredPoints: reward.pointsCost,
        rewardValue: reward.value,
        rewardType:
          reward.type === "discount" ? 1 : reward.type === "freeItem" ? 2 : 3,
        validFrom: reward.validUntil ? undefined : undefined,
        validUntil: normalizedValidUntil,
        termsAndConditions: reward.terms?.join("\n"),
        maxRedemptionsPerCustomer: normalizedMaxRedemptions,
        isActive: reward.isActive,
      } as any;

      const created = await loyaltyProgramApi.createReward(
        currentToken,
        createDto
      );
      return created.rewardId;
    } catch (error) {
      throw error;
    }
  },

  // Customer Registration
  async registerCustomer(
    customerData?: CustomerRegistrationData,
    token?: string,
    restaurantId?: number
  ): Promise<{ customer: Customer; customerId: string }> {
    if (!customerData) throw new Error("Customer data required");
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const createData: CreateCustomerDto = {
        restaurantId: currentRestaurantId,
        fullName: customerData.name,
        phoneNumber: customerData.phone,
        email: customerData.email,
        dateOfBirth: customerData.dateOfBirth
          ? new Date(customerData.dateOfBirth)
          : undefined,
      };
      const newCustomer = await customerApi.createCustomer(
        currentToken,
        createData
      );
      const mappedCustomer = mapCustomerToLegacyFormat(newCustomer);
      return { customer: mappedCustomer, customerId: newCustomer.customerId };
    } catch (error) {
      throw error;
    }
  },

  async checkDuplicateCustomer(phone?: string, email?: string, token?: string, restaurantId?: number): Promise<boolean> {
    const searchTerm = (phone || email)?.trim();
    if (!searchTerm) return false;
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const customers = await customerApi.searchCustomers(
        currentToken,
        currentRestaurantId,
        searchTerm
      );
      
      if (phone) {
        return customers.some(c => c.phoneNumber === phone);
      }
      if (email) {
        const normalizedEmail = email.trim().toLowerCase();
        return customers.some(c => c.email?.trim().toLowerCase() === normalizedEmail);
      }
      
      return customers.length > 0;
    } catch (error) {
      return false;
    }
  },

  async searchCustomers(searchTerm?: string, token?: string, restaurantId?: number): Promise<CustomerSearchResult[]> {
    if (!searchTerm) return [];
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const customers = await customerApi.searchCustomers(
        currentToken,
        currentRestaurantId,
        searchTerm
      );
      return customers.map((customer) => ({
        id: customer.customerId,
        name: customer.fullName,
        phone: customer.phoneNumber,
        email: customer.email || "",
        points: customer.currentPointBalance,
        loyaltyTier: {
          id: (customer.tier || "bronze").toLowerCase(),
          name: customer.tier || "Bronze",
          minPoints: 0,
          maxPoints: 1000,
          benefits: ["Basic benefits"],
          multiplier: 1,
          color: "#CD7F32",
          icon: getTierIcon(customer.tier || "Bronze"),
        },
        totalSpent: customer.totalSpent,
        visitCount: customer.visitCount
      }));
    } catch (error) {
      return [];
    }
  },

  // Loyalty Program Settings
  async getLoyaltySettings(token?: string, restaurantId?: number): Promise<LoyaltyProgramSettings | null> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const [program, tiers] = await Promise.all([
        loyaltyProgramApi.getLoyaltyProgram(currentToken, currentRestaurantId),
        loyaltyApi.getLoyaltyTiers(token, restaurantId)
      ]);

      // Check if program is configured
      if (!program || program.isConfigured === false) {
        return null;
      }

      const settings = mapLoyaltyProgramToSettings(program);
      if (tiers && tiers.length > 0) {
        settings.tiers = tiers;
      }

      return settings;
    } catch (error) {
      // If program doesn't exist or error occurs, return null to indicate not configured
      return null;
    }
  },

  async updateLoyaltySettings(
    settings?: LoyaltyProgramSettings,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!settings) return false;
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
        const normalizedPointsPerRupee = Number(settings.pointsPerRupee ?? 0);
        const normalizedMinimumOrderAmount = Number(
          settings.minimumOrderAmount ?? 0
        );
        const normalizedPointExpiryDays = Number(
          settings.pointsExpireDays ?? 365
        );

        if (!Number.isFinite(normalizedPointsPerRupee) || normalizedPointsPerRupee <= 0) {
          return false;
        }

        const pointExpiryDays =
          Number.isFinite(normalizedPointExpiryDays) &&
            normalizedPointExpiryDays >= 30
            ? Math.round(normalizedPointExpiryDays)
            : 365;

        const minimumOrderAmount =
          Number.isFinite(normalizedMinimumOrderAmount) &&
            normalizedMinimumOrderAmount >= 0
            ? normalizedMinimumOrderAmount
            : 0;

        try {
          const program = await loyaltyProgramApi.getLoyaltyProgram(
            currentToken,
            currentRestaurantId
          );

          // Check if program exists and is configured
          // If not configured or has empty ID, skip update and go to create
          if (!program || !program.isConfigured || program.programId === "00000000-0000-0000-0000-000000000000" || program.programId === "") {
            throw new Error("No existing configured program");
          }

          const toNumber = (v: any, fallback: number) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : fallback;
          };

          const updateData: UpdateLoyaltyProgramDto = {
            programName: program.programName || "Default Loyalty Program",
            pointsPerRupee: normalizedPointsPerRupee,
            minimumOrderAmount: toNumber(
              settings.minimumOrderAmount,
              program.minimumOrderAmount || 0
            ),
            pointExpiryDays: pointExpiryDays,
            isActive: settings.isActive ?? program.isActive ?? true,
            signupBonus: toNumber(
              settings.signupBonus ?? program.signupBonus,
              program.signupBonus || 0
            ),
            birthdayPoints: toNumber(
              settings.birthdayPoints ?? program.birthdayPoints,
              program.birthdayPoints || 0
            ),
            reviewPoints: toNumber(
              settings.reviewPoints ?? program.reviewPoints,
              program.reviewPoints || 0
            ),
            minimumRedemptionPoints: toNumber(
              settings.minimumRedemptionPoints ?? program.minimumRedemptionPoints,
              program.minimumRedemptionPoints || 0
            ),
            referralPointsReferrer: toNumber(
              settings.referralPointsReferrer ?? program.referralPointsReferrer,
              program.referralPointsReferrer || 0
            ),
            referralPointsReferee: toNumber(
              settings.referralPointsReferee ?? program.referralPointsReferee,
              program.referralPointsReferee || 0
            ),
            equivalentLoyaltyPoints: toNumber(
              settings.equivalentLoyaltyPoints ?? program.equivalentLoyaltyPoints,
              program.equivalentLoyaltyPoints || 100
            ),
            rupeesDiscountValue: toNumber(
              settings.rupeesDiscountValue ?? program.rupeesDiscountValue,
              program.rupeesDiscountValue || 50
            ),
          };

          await loyaltyProgramApi.updateLoyaltyProgram(
            currentToken,
            program.programId,
            updateData
          );
          return true;
        } catch (fetchErr) {
          const toNumber2 = (v: any, fallback: number) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : fallback;
          };

          const createDto: CreateLoyaltyProgramDto = {
            restaurantId: currentRestaurantId,
            programName: "Default Loyalty Program",
            pointsPerRupee: normalizedPointsPerRupee,
            minimumOrderAmount: minimumOrderAmount,
            pointExpiryDays: pointExpiryDays,
            isActive: settings.isActive ?? true,
            signupBonus: toNumber2(settings.signupBonus, 0),
            birthdayPoints: toNumber2(settings.birthdayPoints, 0),
            reviewPoints: toNumber2(settings.reviewPoints, 0),
            minimumRedemptionPoints: toNumber2(
              settings.minimumRedemptionPoints,
              0
            ),
            referralPointsReferrer: toNumber2(settings.referralPointsReferrer, 0),
            referralPointsReferee: toNumber2(settings.referralPointsReferee, 0),
            equivalentLoyaltyPoints: toNumber2(settings.equivalentLoyaltyPoints, 100),
            rupeesDiscountValue: toNumber2(settings.rupeesDiscountValue, 50),
          };


          await loyaltyProgramApi.createLoyaltyProgram(currentToken, createDto);
          return true;
        }
      } catch (error) {
        return false;
      }
    },

    // Tier Management
  async getLoyaltyTiers(token?: string, restaurantId?: number): Promise<import("../types/loyalty.types").LoyaltyTier[]> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      return await getConfiguredTiersWithCache(currentToken, currentRestaurantId);
    } catch (error) {
      return [];
    }
  },

  async updateLoyaltyTiers(tiers?: import("../types/loyalty.types").LoyaltyTier[], token?: string, restaurantId?: number): Promise<boolean> {
    if (!tiers) return false;
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const tiersDto = tiers.map(t => ({
        tierId: t.id.length === 36 ? t.id : "00000000-0000-0000-0000-000000000000",
        restaurantId: currentRestaurantId,
        tierName: t.name,
        minimumPoints: Number(t.minPoints),
        pointsMultiplier: Number(t.multiplier),
        colorHex: t.color,
        description: t.benefits.join("|")
      }));

      await loyaltyProgramApi.updateLoyaltyTiers(currentToken, currentRestaurantId, tiersDto);
      invalidateTierCache(currentRestaurantId);
      return true;
    } catch (error) {
      return false;
    }
  },

  // Preview points for a given order amount using backend calculate endpoint
  async previewPoints(
    orderAmount?: number,
    token?: string,
    restaurantId?: number
  ): Promise<
    import("../types/backend-api.types").PointsCalculationResponse | null
  > {
    if (orderAmount === undefined) return null;
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const calc = await pointsApi.calculatePoints(currentToken, {
        restaurantId: currentRestaurantId,
        orderAmount,
      });
      return calc;
    } catch (error) {
      return null;
    }
  },

  // Award points after successful payment — backend authoritative
  async awardPoints(
    customerId?: string,
    orderAmount?: number,
    description?: string,
    token?: string,
    restaurantId?: number
  ): Promise<import("../types/backend-api.types").TransactionDto | null> {
    if (!customerId || orderAmount === undefined) return null;
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const awardDto: import("../types/backend-api.types").AwardPointsDto = {
        customerId,
        restaurantId: currentRestaurantId,
        orderAmount,
        description,
        createdBy: undefined,
      };

      const tx = await transactionApi.awardPoints(currentToken, awardDto);
      return tx;
    } catch (error) {
      return null;
    }
  },

  // Dashboard and Statistics
  async getDashboardStats(token?: string, restaurantId?: number): Promise<DashboardStats> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
        let analytics: any = null;
        try {
          analytics = await loyaltyProgramApi.getLoyaltyAnalytics(
            currentToken,
            currentRestaurantId
          );
        } catch (err) {
          // Continue with fallback
        }

        if (analytics) {
          return {
            totalCustomers: analytics.totalCustomers || 0,
            pointsIssuedToday: Math.floor(
              (analytics.totalPointsIssued || 0) / 30
            ),
            pointsRedeemedToday: Math.floor(
              (analytics.totalPointsRedeemed || 0) / 30
            ),
            newCustomersToday: Math.floor((analytics.totalCustomers || 0) / 30),
            averagePointsPerCustomer: analytics.averagePointsPerCustomer || 0,
          };
        }

        try {
          const customers = await customerApi.getCustomers(
            currentToken,
            1,
            1,
            currentRestaurantId
          );
          const totalCustomers = customers.totalCount || 0;
          return {
            totalCustomers,
            pointsIssuedToday: 0,
            pointsRedeemedToday: 0,
            newCustomersToday: 0,
            averagePointsPerCustomer: 0,
          };
        } catch (customerErr) {
        }

        return {
          totalCustomers: 0,
          pointsIssuedToday: 0,
          pointsRedeemedToday: 0,
          newCustomersToday: 0,
          averagePointsPerCustomer: 0,
        };
      } catch (error) {
        return {
          totalCustomers: 0,
          pointsIssuedToday: 0,
          pointsRedeemedToday: 0,
          newCustomersToday: 0,
          averagePointsPerCustomer: 0,
        };
      }
    },

  async getRecentActivity(token?: string, restaurantId?: number): Promise<RecentActivity[]> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const transactions = await transactionApi.getTransactions(
        currentToken,
        currentRestaurantId,
        1,
        10
      );
        return transactions.items.map((transaction) => ({
          id: transaction.transactionId,
          type:
            transaction.transactionType === TransactionType.EARNED
              ? ("points_earned" as const)
              : ("points_redeemed" as const),
          customerName: transaction.customerName || "Unknown Customer",
          description: transaction.description || "Loyalty transaction",
          timestamp: transaction.transactionDate.toString(),
          points: transaction.pointsChanged,
        }));
    } catch (error) {
      return [];
    }
  },

  // Transaction detail & reversal
  async getTransactionDetails(
    transactionId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<TransactionDetailDto | null> {
    if (!transactionId) return null;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      const detail = await transactionApi.getTransactionDetails(
        currentToken,
        transactionId
      );
      return detail;
    } catch (error) {
      return null;
    }
  },

  async reverseTransaction(transactionId?: string, token?: string, restaurantId?: number): Promise<boolean> {
    if (!transactionId) return false;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      try {
        await transactionApi.reverseTransaction(currentToken, transactionId);
        return true;
      } catch (err) {
        await transactionApi.reverseTransactionFallback(
          currentToken,
          transactionId
        );
        return true;
      }
    } catch (error) {
      return false;
    }
  },

  // Points Processing
  async processPointsTransaction(
    transaction?: PointsTransactionRequest,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!transaction) return false;
    const { currentToken, currentRestaurantId, currentUserName } = getAuthContext(token, restaurantId);
    try {
      if (transaction.type === "earn") {
          const awardData: AwardPointsDto = {
            customerId: transaction.customerId,
            restaurantId: currentRestaurantId,
            orderAmount: transaction.orderAmount || 0,
            description: transaction.description || "Points transaction",
            createdBy: currentUserName,
          };
          await transactionApi.awardPoints(currentToken, awardData);
        } else {
          const adjustmentData: ManualAdjustmentDto = {
            customerId: transaction.customerId,
            restaurantId: currentRestaurantId,
            pointsChanged:
              transaction.type === "redeem"
                ? -transaction.points
                : transaction.points,
            description: transaction.description || "Points transaction",
            createdBy: currentUserName,
          };
        await transactionApi.manualAdjustment(currentToken, adjustmentData);
      }
      return true;
    } catch (error) {
      return false;
    }
  },

  // Notification Methods
  async sendPointsReminder(
    reminderData?: PointsReminderRequestDto,
    token?: string,
    restaurantId?: number
  ): Promise<any> {
    if (!reminderData) throw new Error("Reminder data required");
    const { currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      return await notificationApi.sendPointsReminder(
        currentRestaurantId,
        reminderData
      );
    } catch (error) {
      throw error;
    }
  },

  async sendTierProgressNotification(
    notificationData?: TierProgressNotificationRequestDto,
    token?: string,
    restaurantId?: number
  ): Promise<any> {
    if (!notificationData) throw new Error("Notification data required");
    const { currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      return await notificationApi.sendTierProgressNotification(
        currentRestaurantId,
        notificationData
      );
    } catch (error) {
      throw error;
    }
  },

  async sendPromotionalNotification(
    notificationData?: PromotionalNotificationRequestDto,
    token?: string,
    restaurantId?: number
  ): Promise<any> {
    if (!notificationData) throw new Error("Notification data required");
    const { currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      return await notificationApi.sendPromotionalNotification(
        currentRestaurantId,
        notificationData
      );
    } catch (error) {
      throw error;
    }
  },

  // Legacy method stubs
  calculatePointsEarned(
    orderAmount?: number,
    customerTier?: string,
    token?: string,
    restaurantId?: number
  ): number {
    if (!orderAmount) return 0;
    return Math.floor(orderAmount * 1);
  },

  calculateCustomerTier(
    totalPoints?: number,
    token?: string,
    restaurantId?: number
  ): string | null {
    // Tiers are configured per-restaurant — there is no hardcoded mapping.
    // Use getLoyaltyTiers() + points to determine tier dynamically.
    return null;
  },

  async calculateTierProgress(
    customerId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<any> {
    if (!customerId) return null;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      return await customerApi.getCustomerTierProgress(currentToken, customerId);
      } catch (error) {
        return null;
      }
    },

    // Analytics
  async getCustomerAnalytics(
    token?: string,
    restaurantId?: number
  ): Promise<CustomerAnalytics> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      let analytics: any = null;
      try {
        analytics = await loyaltyProgramApi.getLoyaltyAnalytics(
          currentToken,
          currentRestaurantId
        );
      } catch (err) {
        // Note: Loyalty analytics endpoint not available, using fallback data
        analytics = null;
      }

      let customers: CustomerDto[] = [];
      let customersTotalCount: number | null = null;
      let configuredTiers: import("../types/loyalty.types").LoyaltyTier[] = [];

      // Fetch customers and real configured tiers in parallel
      try {
        const [custPage, tiers] = await Promise.all([
          customerApi.getCustomers(currentToken, 1, 100, currentRestaurantId),
          getConfiguredTiersWithCache(currentToken, currentRestaurantId),
        ]);
        customers = custPage.items || [];
        customersTotalCount =
          typeof custPage.totalCount === "number" ? custPage.totalCount : null;
        if (tiers && tiers.length > 0) {
          configuredTiers = tiers;
        }
      } catch (err) {
        // Individual failures handled below via empty fallbacks
      }

      const topSpenders = (customers || [])
        .map(mapCustomerToLegacyFormat)
        .sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0))
        .slice(0, 10);

      let tierDistribution: { [tier: string]: number } = {};

      // Recalculate tier distribution from live API tiers + live customer data.
      // Uses real configured thresholds so the widget stays in sync whenever
      // tiers are added, renamed, or deleted.
      if (customers && customers.length > 0) {
        const tiers = configuredTiers;
        if (tiers.length > 0) {
          // Initialise all configured tiers at 0
          tiers.forEach((t) => (tierDistribution[t.name] = 0));
          customers.forEach((c) => {
            const points = (c as any).currentPointBalance || 0;
            const sorted = [...tiers].sort((a, b) => b.minPoints - a.minPoints);
            const matched = sorted.find((t) => points >= t.minPoints);
            if (matched) {
              tierDistribution[matched.name] = (tierDistribution[matched.name] || 0) + 1;
            }
            // Customers below the lowest threshold are not counted in any tier
          });
        }
      }

      const totalCustomersFallback =
        (analytics as any)?.totalCustomers ||
        customersTotalCount ||
        (customers && customers.length) ||
        0;
      const activeCustomersFallback =
        (analytics as any)?.activeCustomers ??
        (customers ? customers.filter((c) => (c as any).isActive).length : 0);

      return {
        totalCustomers:
          typeof (analytics as any)?.totalCustomers === "number"
            ? (analytics as any).totalCustomers
            : totalCustomersFallback,
        activeCustomers:
          typeof (analytics as any)?.activeCustomers === "number"
            ? (analytics as any).activeCustomers
            : activeCustomersFallback,
        newCustomersThisMonth:
          typeof (analytics as any)?.newCustomersThisMonth === "number"
            ? (analytics as any).newCustomersThisMonth
            : Math.floor(totalCustomersFallback * 0.1),
        churnRate:
          typeof (analytics as any)?.customerRetentionRate === "number"
            ? 100 - (analytics as any).customerRetentionRate
            : 0,
        averageLifetimeValue:
          (typeof (analytics as any)?.totalRevenueGenerated === "number"
            ? (analytics as any).totalRevenueGenerated
            : 0) / (totalCustomersFallback || 1) || 0,
        averagePointsPerCustomer:
          (analytics as any)?.averagePointsPerCustomer || 0,
        topSpenders,
        tierDistribution,
        engagementMetrics: {
          averageVisitFrequency: (analytics as any).averageVisitFrequency || 0,
          averageOrderValue: (analytics as any)?.totalRevenueGenerated
            ? (analytics as any).totalRevenueGenerated /
            ((totalCustomersFallback || 1) * 10)
            : 0,
          seasonalTrends: (analytics as any).seasonalTrends || {},
        },
        pointsActivityData: [],
        dailyEngagementData: [],
        topPerformingDays: [],
      };
    } catch (error) {
      try {
        const custPage = await customerApi.getCustomers(
          currentToken,
          1,
          100,
          currentRestaurantId
        );
        const customers = custPage.items || [];
        const total =
          typeof custPage.totalCount === "number"
            ? custPage.totalCount
            : customers.length;
        const active = customers.filter((c) => (c as any).isActive).length;
        const mapped = customers
          .map(mapCustomerToLegacyFormat)
          .slice(0, 10);

        return {
          totalCustomers: total,
          activeCustomers: active,
          newCustomersThisMonth: Math.max(0, Math.floor(total * 0.05)),
          churnRate: 0,
          averageLifetimeValue: 0,
          averagePointsPerCustomer: 0,
          topSpenders: mapped,
          tierDistribution: {},
          engagementMetrics: {
            averageVisitFrequency: 0,
            averageOrderValue: 0,
            seasonalTrends: {},
          },
          pointsActivityData: [],
          dailyEngagementData: [],
          topPerformingDays: [],
        };
      } catch (err2) {
        return {
          totalCustomers: 0,
          activeCustomers: 0,
          newCustomersThisMonth: 0,
          churnRate: 0,
          averageLifetimeValue: 0,
          averagePointsPerCustomer: 0,
          topSpenders: [],
          tierDistribution: {},
          engagementMetrics: {
            averageVisitFrequency: 0,
            averageOrderValue: 0,
            seasonalTrends: {},
          },
          pointsActivityData: [],
          dailyEngagementData: [],
          topPerformingDays: [],
        };
      }
    }
  },

  // Placeholder methods
  async getPromotions(
    token?: string,
    restaurantId?: number
  ): Promise<Promotion[]> {
    return [];
  },

  async createPromotion(
    promotion?: Omit<Promotion, "id" | "currentUsage">,
    token?: string,
    restaurantId?: number
  ): Promise<string> {
    return Promise.resolve(`promo_${Date.now()}`);
  },

  async togglePromotionStatus(
    promotionId?: string,
    isActive?: boolean,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    return Promise.resolve(true);
  },

  async getChallenges(
    token?: string,
    restaurantId?: number
  ): Promise<GameificationChallenge[]> {
    return [];
  },

  async createChallenge(
    challenge?: Omit<
      GameificationChallenge,
      "id" | "participantCount" | "completedCount"
    >,
    token?: string,
    restaurantId?: number
  ): Promise<string> {
    return Promise.resolve(`challenge_${Date.now()}`);
  },

  async getCustomerSegments(
    token?: string,
    restaurantId?: number
  ): Promise<CustomerSegment[]> {
    return [];
  },

  async updateReward(
    rewardId?: string,
    rewardData?: any,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!rewardId || !rewardData) {
      throw new Error("Reward ID and reward data are required.");
    }
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      const normalizedMaxRedemptions = normalizeMaxRedemptions(
        rewardData.maxRedemptions
      );
      const normalizedValidUntil = normalizeDateInputValue(rewardData.validUntil);

      const updateDto = {
        rewardName: rewardData.name,
        description: rewardData.description,
        requiredPoints: rewardData.pointsCost,
        rewardValue: rewardData.value,
        rewardType:
          rewardData.type === "discount"
            ? 1
            : rewardData.type === "freeItem"
              ? 2
              : 3,
        validUntil: normalizedValidUntil,
        termsAndConditions: rewardData.terms?.join("\n"),
        maxRedemptionsPerCustomer: normalizedMaxRedemptions,
        isActive: rewardData.isActive,
      } as any;

      await loyaltyProgramApi.updateReward(currentToken, rewardId, updateDto);
      return true;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update reward.";
      throw new Error(message);
    }
  },

  async toggleRewardStatus(
    rewardId?: string,
    isActive?: boolean,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!rewardId) {
      throw new Error("Reward ID is required.");
    }
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      await loyaltyProgramApi.toggleRewardStatus(currentToken, rewardId);
      return true;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update reward status.";
      throw new Error(message);
    }
  },

  async deleteReward(
    rewardId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!rewardId) {
      throw new Error("Reward ID is required.");
    }
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      await loyaltyProgramApi.deleteReward(currentToken, rewardId);
      return true;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete reward.";
      throw new Error(message);
    }
  },

  async triggerBirthdayOffers(
    token?: string,
    restaurantId?: number
  ): Promise<number> {
    return Promise.resolve(0);
  },

  async triggerInactivityCampaign(
    token?: string,
    restaurantId?: number
  ): Promise<number> {
    return Promise.resolve(0);
  },

  async processReferral(
    referrerId?: string,
    refereeEmail?: string,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    return Promise.resolve(true);
  },

  async awardReviewPoints(
    customerId?: string,
    rating?: number,
    token?: string,
    restaurantId?: number
  ): Promise<number> {
    if (!customerId || !rating) return 0;
    const { currentToken, currentRestaurantId, currentUserName } = getAuthContext(token, restaurantId);
    const pointsToAward = rating >= 4 ? 50 : 0;
    if (pointsToAward > 0) {
      try {
        const adjustmentData: ManualAdjustmentDto = {
          customerId,
          restaurantId: currentRestaurantId,
          pointsChanged: pointsToAward,
          description: `Review points for ${rating}-star rating`,
          createdBy: currentUserName,
        };
        await transactionApi.manualAdjustment(currentToken, adjustmentData);
      } catch (error) {
      }
    }
    return pointsToAward;
  },

  // Campaign methods
  async getCampaigns(
    token?: string,
    restaurantId?: number
  ): Promise<PromotionalCampaign[]> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const resp = await promotionsApi.getCampaigns(
        currentToken,
        currentRestaurantId,
        1,
        100
      );
      return resp.items || [];
    } catch (error) {
      return [];
    }
  },

  async createCampaign(
    campaignData?: Omit<PromotionalCampaign, "id" | "createdAt" | "metrics">,
    token?: string,
    restaurantId?: number
  ): Promise<string> {
    if (!campaignData) throw new Error("Campaign data required");
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const created = await promotionsApi.createCampaign(currentToken, {
        ...campaignData,
        restaurantId: currentRestaurantId,
      });
      return created?.campaignId || created?.id || created;
    } catch (error) {
      throw error;
    }
  },

  async getCampaignById(
    campaignId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<PromotionalCampaign | null> {
    if (!campaignId) return null;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      const resp = await promotionsApi.getCampaignById(currentToken, campaignId);
      return resp || null;
    } catch (error) {
      return null;
    }
  },

  async updateCampaign(
    campaignId?: string,
    updates?: Partial<PromotionalCampaign>,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!campaignId || !updates) return false;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      await promotionsApi.updateCampaign(currentToken, campaignId, updates);
      return true;
    } catch (error) {
      return false;
    }
  },

  async sendCampaign(
    campaignId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!campaignId) return false;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      await promotionsApi.sendCampaign(currentToken, campaignId);
      return true;
    } catch (error) {
      return false;
    }
  },

  async pauseCampaign(
    campaignId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!campaignId) return false;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      await promotionsApi.pauseCampaign(currentToken, campaignId);
      return true;
    } catch (error) {
      return false;
    }
  },

  async deleteCampaign(
    campaignId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    if (!campaignId) return false;
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      await promotionsApi.deleteCampaign(currentToken, campaignId);
      return true;
      } catch (error) {
        return false;
      }
    },

  async getCampaignRecipients(
    campaignId?: string,
    token?: string,
    restaurantId?: number
  ): Promise<CampaignRecipient[]> {
    if (!campaignId) return [];
    const { currentToken } = getAuthContext(token, restaurantId);
    try {
      const resp = await promotionsApi.getCampaignById(currentToken, campaignId);
      return resp?.recipients || [];
    } catch (error) {
      return [];
    }
  },

  async getCampaignDashboardStats(
    token?: string,
    restaurantId?: number
  ): Promise<CampaignDashboardStats> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const resp = await promotionsApi.getCampaigns(
        currentToken,
        currentRestaurantId,
        1,
        100
      );
      const items = resp.items || [];
      return {
        totalCampaigns: items.length,
        activeCampaigns: items.filter((c: any) => c.status === "active").length,
        totalMessagesSent: items.reduce(
          (s: number, c: any) => s + (c.metrics?.totalSent || 0),
          0
        ),
        totalDelivered: items.reduce(
          (s: number, c: any) => s + (c.metrics?.delivered || 0),
          0
        ),
        averageOpenRate: 0,
        averageClickRate: 0,
        campaignsThisMonth: 0,
        messagesThisMonth: 0,
      };
    } catch (error) {
      return {
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalMessagesSent: 0,
        totalDelivered: 0,
        averageOpenRate: 0,
        averageClickRate: 0,
        campaignsThisMonth: 0,
        messagesThisMonth: 0,
      };
    }
  },

  async previewCampaignReach(
    filters?: CampaignFilters,
    token?: string,
    restaurantId?: number
  ): Promise<{ count: number; customers: Customer[] }> {
    if (!filters) return { count: 0, customers: [] };
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const resp = await promotionsApi.previewReach(
        currentToken,
        currentRestaurantId,
        filters
      );
      const customers = (resp?.customers || []).map(mapCustomerToLegacyFormat);
      return { count: resp?.count || 0, customers };
    } catch (error) {
      return { count: 0, customers: [] };
    }
  },

  async getMessageTemplates(
    token?: string,
    restaurantId?: number
  ): Promise<MessageTemplate[]> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      const resp = await promotionsApi.getTemplates(
        currentToken,
        currentRestaurantId
      );
      return resp || [];
    } catch (error) {
      return [];
    }
  },

  async createSpecialEvent(
    eventData?: {
      name: string;
      description: string;
      startDate: string;
      endDate: string;
      rewards: string[];
    },
    token?: string,
    restaurantId?: number
  ): Promise<string> {
    return Promise.resolve(`event_${Date.now()}`);
  },

  async sendNotification(
    customerId?: string,
    message?: string,
    type?: string,
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    return Promise.resolve(true);
  },

  async sendBulkNotifications(
    customerIds?: string[],
    message?: string,
    token?: string,
    restaurantId?: number
  ): Promise<number> {
    return Promise.resolve(customerIds?.length || 0);
  },

  async exportCustomerData(
    format?: "csv" | "excel",
    token?: string,
    restaurantId?: number
  ): Promise<string> {
    return Promise.resolve(`/downloads/customers-${Date.now()}.${format || 'csv'}`);
  },

  async exportLoyaltyReport(
    dateRange?: {
      start: string;
      end: string;
    },
    token?: string,
    restaurantId?: number
  ): Promise<string> {
    return Promise.resolve(`/downloads/loyalty-report-${Date.now()}.pdf`);
  },

  async processPointExpiry(
    token?: string,
    restaurantId?: number
  ): Promise<boolean> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      await pointsApi.processExpiry(currentToken, currentRestaurantId);
      return true;
    } catch (error) {
      return false;
    }
  },

  // Analytics Methods
  async getTimeSeriesAnalytics(
    timeRange?: "1day" | "7days" | "30days" | "custom",
    customStartDate?: string,
    customEndDate?: string,
    token?: string,
    restaurantId?: number
  ): Promise<{
    pointsActivityData: Array<{
      period: string;
      earned: number;
      redeemed: number;
    }>;
    dailyEngagementData: Array<{
      day: string;
      visits: number;
      pointsEarned: number;
      newSignups: number;
    }>;
    topPerformingDays: Array<{
      day: string;
      visits: number;
      revenue: number;
      pointsEarned: number;
      performanceScore: number;
    }>;
  }> {
    const { currentToken, currentRestaurantId } = getAuthContext(token, restaurantId);
    try {
      let apiTimeRange: string;
      switch (timeRange) {
        case "1day":
          apiTimeRange = "1d";
          break;
        case "7days":
          apiTimeRange = "7d";
          break;
        case "30days":
          apiTimeRange = "30d";
          break;
        case "custom":
          if (customStartDate && customEndDate) {
            const start = new Date(customStartDate);
            const end = new Date(customEndDate);
            const daysDiff = Math.ceil(
              (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
            );
            apiTimeRange = `${daysDiff}d`;
          } else {
            apiTimeRange = "30d";
          }
          break;
        default:
          apiTimeRange = "30d";
      }

      const response = await loyaltyAnalyticsApi.getTimeSeriesAnalytics(
        currentToken,
        currentRestaurantId,
        apiTimeRange,
        customStartDate,
        customEndDate
      );

      const pointsActivityData = response.pointsActivity.map(
        (point: any) => {
          const dateStr = point.date || point.period || new Date().toISOString();
          const date = new Date(dateStr);
          const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
          ];

          let period = point.period;
          if (!isNaN(date.getTime())) {
            period = `${monthNames[date.getMonth()]} ${date.getDate()}`;
          }

          return {
            period: period || "Unknown",
            earned: Number(
              point.pointsEarned ??
              point.PointsEarned ??
              point.earned ??
              point.earnedPoints ??
              point.totalPointsEarned ??
              0
            ),
            redeemed: Number(
              point.pointsRedeemed ??
              point.PointsRedeemed ??
              point.redeemed ??
              point.redeemedPoints ??
              point.totalPointsRedeemed ??
              0
            ),
          };
        }
      );

      const dailyEngagementData = response.dailyEngagement.map((item: any) => {

        const date = new Date(item.date);
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const dayOfWeek = days[date.getDay()];

        return {
          day: dayOfWeek,
          visits: item.totalActivities || 0,
          pointsEarned: item.totalPointsEarned || 0,
          newSignups: item.activeCustomers || 0,
        };
      });

      const topPerformingDays = response.topPerformingDays.map((day: any) => ({
        day: day.dayOfWeek,
        visits: day.visits,
        revenue: day.revenue,
        pointsEarned: day.pointsEarned,
        performanceScore: day.performanceScore,
      }));

      const result = {
        pointsActivityData,
        dailyEngagementData,
        topPerformingDays,
      };

      return result;
    } catch (error) {
      return {
        pointsActivityData: [],
        dailyEngagementData: [],
        topPerformingDays: [],
      };
    }
  },
};

// For backward compatibility, export a default instance
export default loyaltyApi;
